import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  derivedOutcome,
  finalizeAnswers,
  flattenItems,
  type AnswerMap,
  type FinalizeError,
} from "@njbsoft/form-logic";
import { FormRenderer, parseFormDefinition } from "@njbsoft/form-renderer";
import { finalizeFindingKey } from "@njbsoft/form-composer";
import {
  resolveSupplementContext,
  type StoredAssignment,
  type StoredSupplement,
} from "@njbsoft/field-sync";
import { uuidv7 } from "@njbsoft/utils";
import {
  Badge,
  Button,
  Card,
  DialogShell,
  EmptyState,
  FormField,
  PageSkeleton,
  Select,
} from "@/components/ui";
import { pruneErrorMap, reduceAnswer } from "@/components/tester/execution/answer-reducer";
import {
  onlineDeviceCategory,
  onlineReadContext,
  type OnlineReadContext,
} from "@/components/tester/execution/online-arm";
import { showSupplementPicker } from "@/components/tester/execution/supplement-routing";
import { useCopyFrom } from "@/hooks/use-copy-from";
import { useFacilityDevices } from "@/hooks/use-facility-devices";
import { useInvalidateAfterFieldworkRun } from "@/hooks/use-fieldwork-sync";
import { useIsOnline } from "@/hooks/use-is-online";
import { useMyCompanies } from "@/hooks/use-my-companies";
import { useMyWorkAssignment } from "@/hooks/use-my-work-assignment";
import { useToast } from "@/hooks/use-toast";
import { useFieldworkStorage } from "@/lib/fieldwork/storage-provider";

interface SupplementSearch {
  /** The parent regulatory capture's client submission id — the enqueue's replay-order anchor. */
  parent: string;
  company?: string;
}

/** A settled-but-empty online read context — the failed-roster arm. */
const EMPTY_ONLINE_CTX: OnlineReadContext = {
  facilityValues: {},
  deviceValues: {},
  collections: {},
};

export const Route = createFileRoute("/_company/my-work/$assignmentId/supplement")({
  validateSearch: (search: Record<string, unknown>): SupplementSearch => ({
    parent: typeof search.parent === "string" ? search.parent : "",
    company: typeof search.company === "string" ? search.company : undefined,
  }),
  component: TesterSupplementPage,
});

/**
 * The company-private second step after the regulatory submission (spec
 * §11.6). A branded screen — visually distinct from the utility form — renders
 * the matched company supplement over the shared renderer. The parent
 * regulatory submission was already enqueued on the execution screen — and
 * while online its awaited drain has already RUN, so the pull may have pruned
 * the assignment from the bundle before this screen reads it: the parent is
 * dual-sourced (server assignment + facility-devices read online, bundle row
 * offline). This step is skippable and never gates the regulatory record.
 * Answers complete in-session — NO draft, NO autosave, abandoning the screen
 * IS the skip; a persisted half-answered draft would imply the company-private
 * step is owed — and enqueue to the replay-safe supplement outbox, which
 * pushes after the parent.
 */
function TesterSupplementPage() {
  const { t, i18n } = useTranslation(["field-work", "common"]);
  const { t: tFindings } = useTranslation("test-entry");
  const navigate = useNavigate();
  const toast = useToast();
  const { assignmentId } = Route.useParams();
  const { parent, company } = Route.useSearch();
  const { storage, engine } = useFieldworkStorage();
  const { companies: myCompanies, isLoading: companiesLoading } = useMyCompanies();
  const isOnline = useIsOnline();
  // The drained send's completion marks the run's query families stale
  // through the shared settle invalidation — reached through this wrapper
  // (the query-client literal stays in the hook's file, outside the fill-path
  // boundary wall's scanned roots), the same shape the execute screen's
  // completions use.
  const invalidateAfterRun = useInvalidateAfterFieldworkRun();

  const [bundleRow, setBundleRow] = useState<StoredAssignment | null>(null);
  const [deviceCategory, setDeviceCategory] = useState<string | null>(null);
  const [localReadContext, setLocalReadContext] = useState<Record<string, string>>({});
  const [pickedCompanyId, setPickedCompanyId] = useState<string | null>(company ?? null);
  const [supplement, setSupplement] = useState<StoredSupplement | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [errors, setErrors] = useState<Map<string, FinalizeError["rule"]> | null>(null);
  const [skipVisible, setSkipVisible] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const answersRef = useRef<AnswerMap>({});

  // The online arm's parent, DUAL-SOURCED like the detail screen: an awaited
  // send's drain ends with a pull that prunes the just-submitted assignment
  // from the bundle (the field bundle selects assigned/in_progress only and
  // the replace wipes the store wholesale), so while connected the SERVER row
  // is the parent of record — the assignment read the detail screen uses. The
  // deviceCategory and the read context come off the facility-devices read
  // (the read-context wire): its roster row's device_category is the same enum
  // the pinned version's category — which the match keys on — carries, and
  // its read_context is the device's keys. Offline the bundle row stays the
  // parent by construction — no run can have pruned it.
  const assignmentQuery = useMyWorkAssignment(isOnline ? assignmentId : null);
  const wireRow = assignmentQuery.data ?? null;
  const devicesQuery = useFacilityDevices(isOnline && wireRow !== null ? wireRow.facility_id : "");
  const devicesView = devicesQuery.data ?? null;
  const devicesFailed = devicesQuery.isError;
  const onlineCtx = useMemo(() => {
    if (wireRow === null) return null;
    // A failed roster read is settled, not pending: identity labels ride the
    // assignment row itself, and the empty context is the same
    // absent-facility arm the offline bundle has for an uncached one.
    if (devicesView === null) return devicesFailed ? EMPTY_ONLINE_CTX : null;
    return onlineReadContext(devicesView, wireRow.device_installation_id ?? null);
  }, [wireRow, devicesView, devicesFailed]);
  // A pending read is not an absent parent: nothing decides (the picker, the
  // empty state, the form) until the arm settles; a FAILED read falls to the
  // local arm below exactly as before.
  const onlineParentPending =
    isOnline &&
    (wireRow === null ? !assignmentQuery.isError : devicesView === null && !devicesFailed);

  // Dual-source per family, the detail screen's precedence: the server parent
  // wins while online and in hand; the bundle row serves offline and whenever
  // the online read failed.
  // Memoized so the outcome and the finalize callback that read them keep
  // their identity across renders that change neither source.
  const parentRow = useMemo<StoredAssignment | null>(
    () => (isOnline && wireRow !== null ? { assignment: wireRow, localStatus: null } : bundleRow),
    [isOnline, wireRow, bundleRow],
  );
  const parentCategory =
    isOnline && wireRow !== null
      ? onlineDeviceCategory(devicesView, wireRow.device_installation_id ?? null)
      : deviceCategory;
  const readContext = useMemo(
    () =>
      isOnline && wireRow !== null && onlineCtx !== null
        ? { ...onlineCtx.facilityValues, ...onlineCtx.deviceValues }
        : localReadContext,
    [isOnline, wireRow, onlineCtx, localReadContext],
  );

  // The offline arm's read chain: the parent assignment (its org_id keys the
  // supplement enqueue), its pinned template version (its deviceCategory
  // drives supplement resolution — WorkAssignment carries no device_category,
  // mirroring the execute screen) and the read context the seal evaluates
  // with: the bundle facility's, with the target device's keys over it. Also
  // the online arm's fallback whenever the server reads fail. Nothing runs
  // before the provider hands over the storage handle; the effect re-runs on
  // its own when the store opens.
  useEffect(() => {
    if (storage === null) return;
    let cancelled = false;
    void (async () => {
      const row = await storage.getBundleAssignment(assignmentId);
      const [version, target, facility] = row
        ? await Promise.all([
            storage.getTemplateVersion(row.assignment.template_version_id),
            storage.getTarget(row.assignment.facility_id, row.assignment.device_installation_id),
            storage.getBundleFacility(row.assignment.facility_id),
          ])
        : [null, null, null];
      if (cancelled) return;
      setBundleRow(row);
      setDeviceCategory(version?.deviceCategory ?? null);
      setLocalReadContext({ ...facility?.readContext, ...target?.device_read_context });
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [storage, assignmentId]);

  // On direct navigation `company` may be unset; re-resolve from the parent's
  // stamped company (whichever arm holds it), falling back to the picker. A
  // parent with no stamped company still resolves through the sole-membership
  // arm — the assignment binding is only the first link of the chain.
  const assignedCompanyId = parentRow?.assignment.assigned_to_company_id ?? null;
  const assignedCompanyName = parentRow?.assignment.assigned_to_company_name ?? null;
  const hasParentRow = parentRow !== null;
  useEffect(() => {
    if (company || !hasParentRow) return;
    const resolution = resolveSupplementContext({
      assignment: {
        assignedToCompanyId: assignedCompanyId,
        assignedToCompanyName: assignedCompanyName,
      },
      companies: myCompanies,
    });
    if (resolution.kind === "resolved") setPickedCompanyId(resolution.companyId);
  }, [company, hasParentRow, assignedCompanyId, assignedCompanyName, myCompanies]);

  // Resolve the supplement for the picked company + the parent's device
  // category. Re-runs when the tester picks a company on the pick arm.
  useEffect(() => {
    if (storage === null) return;
    let cancelled = false;
    void (async () => {
      if (!pickedCompanyId) {
        if (!cancelled) setSupplement(null);
        return;
      }
      const match = await storage.supplementForCompany(pickedCompanyId, parentCategory);
      if (!cancelled) setSupplement(match);
    })();
    return () => {
      cancelled = true;
    };
  }, [storage, pickedCompanyId, parentCategory]);

  const companyName = useMemo(() => {
    if (supplement) return supplement.companyName;
    return myCompanies.find((c) => c.id === pickedCompanyId)?.name ?? "";
  }, [supplement, myCompanies, pickedCompanyId]);

  // The wire ships the pinned supplement definition as an open JSON object,
  // and `StoredSupplement` keeps that shape; `parseFormDefinition` is the one
  // sanctioned wire-to-document narrow. Nothing is validated: conformance is
  // the Go publish gate's job, and a client-side re-check would fork that
  // authority.
  const definition = useMemo(
    () => (supplement ? parseFormDefinition(supplement.definition) : null),
    [supplement],
  );

  // Always live: this screen never renders a sealed document — it IS the
  // editing surface. No `sealedOutcome` branch belongs here.
  const outcomeValue = useMemo(
    () => (definition ? derivedOutcome(definition, answers, readContext) : null),
    [definition, answers, readContext],
  );

  const handleAnswer = useCallback((linkId: string, value: unknown | undefined) => {
    // Shared rebuild-and-prune rules live on `reduceAnswer` / `pruneErrorMap`.
    // The supplement holds answers in memory only, so there is no draft save
    // between the rebuild and the state write.
    const next = reduceAnswer(answersRef.current, linkId, value);
    answersRef.current = next;
    setAnswers(next);
    setErrors((prev) => pruneErrorMap(prev, linkId));
  }, []);

  // Same copy-once causality as the execution screen's composer, over the same
  // reads the renderer draws with. The supplement carries no draft (answers are
  // memory-only), so `enabled` is simply "the load settled" and priming over
  // the empty initial answers is a harmless no-op — but the copy behavior is
  // identical.
  useCopyFrom(definition, handleAnswer, answers, loaded, readContext);

  const companyOptions = useMemo(
    () => myCompanies.map((c) => ({ value: c.id, label: c.name })),
    [myCompanies],
  );

  // Per-item finalize findings for the renderer, localized through the shared
  // rule→key map the composer's offline arm uses (never a re-rolled one).
  const itemsById = useMemo(
    () => (definition ? flattenItems(definition).byId : null),
    [definition],
  );
  const errorFor = useCallback(
    (linkId: string): string | undefined => {
      const rule = errors?.get(linkId);
      if (rule === undefined || itemsById === null) return undefined;
      return tFindings(finalizeFindingKey(rule, itemsById.get(linkId)?.type ?? null));
    },
    [errors, itemsById, tFindings],
  );

  const goBack = useCallback(() => {
    void navigate({ to: "/my-work/$assignmentId", params: { assignmentId } });
  }, [navigate, assignmentId]);

  const handleFinalize = useCallback(async () => {
    if (!definition || !supplement || !parentRow || !pickedCompanyId || !parent) return;
    if (storage === null) return;
    const sealed = finalizeAnswers(definition, answersRef.current, readContext);
    if (!sealed.ok) {
      setErrors(new Map(sealed.errors.map((e) => [e.link_id, e.rule])));
      toast.warning("field-work:form.finalizeBlocked", { count: sealed.errors.length });
      return;
    }
    setFinalizing(true);
    try {
      await storage.enqueueSupplement({
        clientSubmissionId: uuidv7(),
        parentClientSubmissionId: parent,
        companyId: pickedCompanyId,
        templateVersionId: supplement.templateVersionId,
        orgId: parentRow.assignment.org_id,
        stack: "field",
        responses: sealed.responses,
      });
      // The send IS the sync while online: the drain is awaited — through the
      // engine directly, never the sync hook — so the send is done before the
      // landing mounts, and the completion below fires the shared run-settle
      // invalidation so the landing's cached rows refetch on mount instead of
      // serving the pre-send answer through the staleTime window. Offline the
      // row is durable and the next trigger pushes it after its parent, so
      // nothing waits. A fresh run, so one already in flight that drained
      // before the supplement was queued is not its send.
      if (engine !== null && isOnline) await engine.syncFresh();
      invalidateAfterRun();
      goBack();
    } catch (err) {
      console.error("enqueue supplement failed", err);
      toast.apiError(undefined);
    } finally {
      setFinalizing(false);
    }
  }, [
    definition,
    readContext,
    supplement,
    parentRow,
    pickedCompanyId,
    parent,
    storage,
    engine,
    isOnline,
    invalidateAfterRun,
    goBack,
    toast,
  ]);

  const handleSkipConfirm = useCallback(() => {
    setSkipVisible(false);
    goBack();
  }, [goBack]);

  const language: "en" | "es" = i18n.language.startsWith("es") ? "es" : "en";
  const displayCompany = companyName || "—";

  // The local chain settled AND the online parent (when online) settled too —
  // a pending server read is not an absent parent, so the skeleton owns the
  // screen until the arm that will decide it has answered.
  if (!loaded || onlineParentPending) {
    return (
      <div className="p-4 sm:p-8">
        <PageSkeleton />
      </div>
    );
  }

  // No company resolved AND nothing to pick → nothing to do; return to the
  // assignment detail.
  const showPicker = showSupplementPicker(pickedCompanyId, companyOptions.length);

  if (!showPicker && !supplement) {
    // NEVER a terminal claim while the company source is unsettled: with the
    // company rows still pending this arm would read "nothing to do" to a
    // tester the execute screen just routed here on a locally-verified match —
    // and the offered Back IS the skip, irreversibly. The resolved arm
    // (`company` in the URL) never waits: its supplement and name come from
    // the device store.
    if (!company && companiesLoading) {
      return (
        <div className="p-4 sm:p-8">
          <PageSkeleton />
        </div>
      );
    }
    return (
      <div className="p-4 sm:p-8">
        <EmptyState
          title={t("field-work:supplement.title", { company: displayCompany })}
          actionLabel={t("common:back")}
          onAction={goBack}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-8">
      <Card className="border-l-4 border-l-primary">
        <p className="text-lg font-semibold text-on-surface">
          {t("field-work:supplement.title", { company: displayCompany })}
        </p>
        <p className="mt-1 text-sm text-muted">
          {t("field-work:supplement.subtitle", { company: displayCompany })}
        </p>
      </Card>

      {showPicker ? (
        <Card>
          <FormField label={t("field-work:supplement.pickCompany")}>
            <Select
              options={companyOptions}
              value={pickedCompanyId ?? ""}
              onChange={(e) => setPickedCompanyId(e.target.value)}
              placeholder={t("field-work:supplement.pickCompany")}
            />
          </FormField>
        </Card>
      ) : null}

      {/* Picking a company is a prerequisite, not a parallel affordance: the
          form, the outcome chip, the finalize action and the skip row are ALL
          suppressed while the picker is showing. */}
      {definition && !showPicker ? (
        <div className="space-y-4">
          {outcomeValue !== null ? (
            <Badge variant={outcomeValue === "pass" ? "success" : "danger"}>
              {outcomeValue === "pass"
                ? t("field-work:form.outcomePass")
                : t("field-work:form.outcomeFail")}
            </Badge>
          ) : null}
          <FormRenderer
            definition={definition}
            answers={answers}
            onAnswerChange={handleAnswer}
            readContext={readContext}
            language={language}
            errorFor={errorFor}
          />
          <div className="flex flex-col items-stretch gap-2">
            <Button onClick={() => void handleFinalize()} loading={finalizing}>
              {t("field-work:supplement.finalize", { company: displayCompany })}
            </Button>
            <div className="flex justify-center">
              <Button variant="ghost" onClick={() => setSkipVisible(true)}>
                {t("field-work:supplement.skip")}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      <DialogShell
        open={skipVisible}
        onClose={() => setSkipVisible(false)}
        ariaLabelledBy="supplement-skip-title"
      >
        <div className="space-y-4">
          <h2 id="supplement-skip-title" className="text-lg font-semibold text-on-surface">
            {t("field-work:supplement.skipConfirmTitle")}
          </h2>
          <p className="text-sm text-muted">
            {t("field-work:supplement.skipConfirmBody", { company: displayCompany })}
          </p>
          <div className="flex justify-end gap-2 max-sm:flex-wrap">
            <Button variant="secondary" onClick={() => setSkipVisible(false)}>
              {t("common:cancel")}
            </Button>
            <Button onClick={handleSkipConfirm}>{t("field-work:supplement.skip")}</Button>
          </div>
        </div>
      </DialogShell>
    </div>
  );
}
